import { useQuery } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { buildProxyUrl } from '@/react/portainer/registries/queries/build-url';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { RegistryId } from '@/react/portainer/registries/types/registry';

import { queryKeys } from '../query-keys';

import { useAddonImageTags } from './useAddonImageTags';

/** What a repository actually holds, as declared by its manifest. */
export type RepositoryKind = 'chart' | 'image' | 'unknown';

const REPOSITORY_KIND_STALE_TIME = 5 * 60 * 1000;

const HELM_CONFIG_MEDIA_TYPE = 'application/vnd.cncf.helm.config.v1+json';

const IMAGE_CONFIG_MEDIA_TYPES = [
  'application/vnd.oci.image.config.v1+json',
  'application/vnd.docker.container.image.v1+json',
];

// A chart is always a single manifest; a multi-architecture image is an index of
// them, which carries no config of its own.
const INDEX_MEDIA_TYPES = [
  'application/vnd.oci.image.index.v1+json',
  'application/vnd.docker.distribution.manifest.list.v2+json',
];

// Asked for explicitly, and broadly: a chart is an OCI manifest, so requesting
// only the Docker manifest type (as the registry browser does) is the one case
// that would fail to identify a chart.
const MANIFEST_ACCEPT = [
  'application/vnd.oci.image.manifest.v1+json',
  'application/vnd.oci.image.index.v1+json',
  'application/vnd.docker.distribution.manifest.v2+json',
  'application/vnd.docker.distribution.manifest.list.v2+json',
].join(', ');

/**
 * useAddonRepositoryKind reports whether a repository holds a Helm chart or a
 * container image, so a chart picked as an image (or the reverse) can be called out
 * before the install fails.
 *
 * The repository listing carries no artifact type — obtaining it for every
 * repository costs several requests each, which is why the listing is unfiltered.
 * For one chosen repository it is cheap: read its manifest and look at the config
 * media type. A reference is needed to do that, so the newest tag is used; the tag
 * listing is shared with the image field, which needs it anyway.
 *
 * Resolves to 'unknown' rather than an error. This only ever adds a warning, so a
 * registry that will not serve manifests should leave the form alone.
 */
export function useAddonRepositoryKind({
  registryId,
  repository,
  environmentId,
}: {
  registryId?: RegistryId;
  repository: string;
  environmentId?: EnvironmentId;
}) {
  const tagsQuery = useAddonImageTags({
    registryId,
    repository,
    environmentId,
  });
  const reference = tagsQuery.data?.[0];

  return useQuery({
    queryKey: queryKeys.repositoryKind(registryId ?? 0, repository, reference),
    queryFn: () =>
      fetchRepositoryKind({
        registryId: registryId ?? 0,
        repository,
        reference: reference ?? '',
        environmentId,
      }),
    enabled: !!registryId && !!repository && !!reference && !!environmentId,
    retry: false,
    staleTime: REPOSITORY_KIND_STALE_TIME,
  });
}

async function fetchRepositoryKind({
  registryId,
  repository,
  reference,
  environmentId,
}: {
  registryId: RegistryId;
  repository: string;
  reference: string;
  environmentId?: EnvironmentId;
}): Promise<RepositoryKind> {
  try {
    const { data } = await axios.get<{
      mediaType?: string;
      config?: { mediaType?: string };
    }>(`${buildProxyUrl(registryId)}/${repository}/manifests/${reference}`, {
      params: { endpointId: environmentId },
      headers: { Accept: MANIFEST_ACCEPT },
    });

    return classify(data);
  } catch {
    return 'unknown';
  }
}

function classify(manifest: {
  mediaType?: string;
  config?: { mediaType?: string };
}): RepositoryKind {
  if (manifest.mediaType && INDEX_MEDIA_TYPES.includes(manifest.mediaType)) {
    return 'image';
  }

  const configMediaType = manifest.config?.mediaType;
  if (configMediaType === HELM_CONFIG_MEDIA_TYPE) {
    return 'chart';
  }
  if (configMediaType && IMAGE_CONFIG_MEDIA_TYPES.includes(configMediaType)) {
    return 'image';
  }

  return 'unknown';
}
